import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Send, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  "Suite à une annonce",
  "Suite à un avantage membre",
  "Proposition de collaboration",
  "Demande de mise en relation",
  "Autre",
];

export default function ContactRequestModal({ profile, onClose, onSuccess, prefilledReason, relatedType, relatedId }) {
  const [reason, setReason] = useState(prefilledReason || REASONS[0]);
  const [message, setMessage] = useState("");
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch quota
    axios
      .get(`${API}/api/contact-requests/quota`, { withCredentials: true })
      .then(({ data }) => setQuota(data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (message.trim().length < 20) {
      setError("Votre message doit contenir au moins 20 caractères");
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/api/contact-requests`,
        {
          to_profile_id: profile._id,
          message: message.trim(),
          reason,
          related_type: relatedType,
          related_id: relatedId,
        },
        { withCredentials: true }
      );

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,15,35,0.85)" }}>
      <div className="cbm-card w-full max-w-lg p-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex-1">
            <h2 className="font-outfit font-semibold text-white text-lg">Demander le contact</h2>
            <p className="text-cbm-secondary text-sm mt-1">
              {profile.nom_prenom}
              {profile.entreprise && ` · ${profile.entreprise}`}
            </p>
          </div>
          <button onClick={onClose} className="text-cbm-muted hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Quota info */}
        {quota && (
          <div className="bg-navy-darker rounded-lg p-3 mb-4 flex items-center gap-2">
            <AlertCircle size={14} className="text-gold flex-shrink-0" />
            <p className="text-cbm-secondary text-xs">
              Il vous reste <span className="text-gold font-medium">{quota.remaining}</span> demande{quota.remaining > 1 ? "s" : ""} ce mois-ci
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Reason dropdown */}
          <div>
            <label className="cbm-label">Motif de votre demande</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="cbm-input"
              required
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Message textarea */}
          <div>
            <label className="cbm-label">Pourquoi souhaitez-vous contacter cette personne ?</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ex : J'ai vu votre annonce sur [sujet] et je pense pouvoir vous aider sur [point précis].
Ou : Je travaille avec des entreprises comme la vôtre et j'aimerais vous proposer [idée concrète]."
              className="cbm-input h-32 resize-none"
              required
              minLength={20}
            />
            <p className="text-cbm-muted text-xs mt-1.5">
              {message.length}/20 caractères minimum · Plus votre message est précis, plus vous avez de chances d'obtenir une réponse.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-xs">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <button type="button" onClick={onClose} className="cbm-btn-ghost flex-1">
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || message.trim().length < 20 || (quota && quota.remaining === 0)}
              className="cbm-btn-primary flex-1 flex items-center justify-center gap-1.5"
            >
              <Send size={14} />
              {loading ? "Envoi..." : "Envoyer la demande"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
